"use client";

import { useEffect, useMemo, useState } from "react";
import { resolveObjectArray, resolveUnknown, setPathValue } from "./binding";
import { resolveActionContext } from "./helpers";
import { MacaronSelectionItemActiveContext, MacaronSelectionItemScopeContext } from "./scope";
import type { MacaronRenderContext, RecordAny } from "./types";

export function MacaronActionSelectionList({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const items = resolveObjectArray(config.items, ctx.data);
  const actionConfig = (config.action as RecordAny | undefined) || undefined;
  const actionName = typeof actionConfig?.name === "string" ? actionConfig.name : "";
  const selectionPath = typeof (config.selection as RecordAny | undefined)?.path === "string" ? ((config.selection as RecordAny).path as string) : "";
  const initialSelection = useMemo(() => {
    const raw = resolveUnknown(config.selection, ctx.data);
    if (Array.isArray(raw)) return typeof raw[0] === "string" ? (raw[0] as string) : "";
    return typeof raw === "string" ? raw : "";
  }, [config.selection, ctx.data]);
  const [selectedValue, setSelectedValue] = useState(initialSelection);
  useEffect(() => {
    setSelectedValue((current) => (current === initialSelection ? current : initialSelection));
  }, [initialSelection]);
  const locked = selectedValue.trim().length > 0;
  return (
    <div className="flex flex-col gap-2">
      {items.map((item, i) => {
        const value = typeof item.value === "string" ? item.value : "";
        const label = typeof item.label === "string" ? item.label : typeof item.title === "string" ? item.title : `Action ${i + 1}`;
        const childId = typeof item.child === "string" ? item.child : "";
        const active = value.length > 0 && value === selectedValue;
        const disabled = locked && !active;
        return (
          <button
            key={`${label}-${i}`}
            type="button"
            className={`w-full rounded-lg border bg-white px-3 py-2 text-left text-sm ${active ? "border-[#8CA62A]" : "border-[#D0D5DD]"} ${disabled ? "opacity-40" : ""}`}
            disabled={disabled}
            aria-pressed={active}
            onClick={() => {
              if (!value || disabled) return;
              setSelectedValue(value);
              const nextData = selectionPath ? setPathValue(ctx.data, selectionPath, [value]) : ctx.data;
              if (selectionPath) ctx.updateData?.(selectionPath, [value]);
              const context = { ...resolveActionContext(actionConfig, nextData), selectedValue: value, selectedIndex: i };
              ctx.onAction?.(actionName || (typeof item.action === "string" ? item.action : "action_selection"), ctx.componentId, context);
            }}
          >
            <MacaronSelectionItemScopeContext.Provider value={true}>
              <MacaronSelectionItemActiveContext.Provider value={active}>{childId ? ctx.renderChild(childId) : <span className={active ? "text-[#8CA62A]" : "text-[#161615]"}>{label}</span>}</MacaronSelectionItemActiveContext.Provider>
            </MacaronSelectionItemScopeContext.Provider>
          </button>
        );
      })}
    </div>
  );
}
